import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import Header from '../components/common/Header';
import Button from '../components/common/Button';
import './NotFound.css';

interface NotFoundProps {
  errorType?: 'not-found' | 'private' | 'unauthorized';
  message?: string;
}

const NotFound: React.FC<NotFoundProps> = ({ 
  errorType = 'not-found', 
  message 
}) => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const getErrorContent = () => {
    switch (errorType) {
      case 'private':
        return {
          title: 'Competition is Private',
          code: '403',
          description: message || 'This competition is not accessible to you. It may be private or you may not have permission to view it.',
        };
      case 'unauthorized':
        return {
          title: 'Unauthorized',
          code: '401',
          description: message || 'You need to be logged in to view this competition.',
        };
      case 'not-found':
      default:
        return {
          title: 'Competition Not Found',
          code: '404',
          description: message || 'The competition you are looking for does not exist or has been removed.',
        };
    }
  };

  const errorContent = getErrorContent();

  return (
    <div className="not-found-page">
      {isAuthenticated && <Header />}
      
      <div className="not-found-container">
        <div className="not-found-content" data-tour="error-card">
          <div className="not-found-code">{errorContent.code}</div>
          <h1 className="not-found-title">{errorContent.title}</h1>
          <p className="not-found-description">{errorContent.description}</p>
          
          <div className="not-found-actions">
            <Button 
              variant="primary" 
              size="lg" 
              onClick={() => navigate('/dashboard')}
            >
              Dashboard
            </Button>
            <Button 
              variant="outline" 
              size="lg" 
              onClick={() => navigate(-1)}
            >
              Go Back
            </Button>
          </div>
        </div>

        <div className="not-found-illustration">
          <svg viewBox="0 0 200 200" fill="none">
            <circle cx="100" cy="100" r="80" stroke="currentColor" strokeWidth="2" opacity="0.2" />
            <circle cx="100" cy="100" r="60" stroke="currentColor" strokeWidth="2" opacity="0.3" />
            <circle cx="100" cy="100" r="40" stroke="currentColor" strokeWidth="2" opacity="0.4" />
            <path d="M 100 50 L 100 150 M 50 100 L 150 100" stroke="currentColor" strokeWidth="3" opacity="0.5" />
            <circle cx="100" cy="100" r="8" fill="currentColor" opacity="0.5" />
          </svg>
        </div>
      </div>
    </div>
  );
};

export default NotFound;
